import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { UserService } from '../services/user.service';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { AuthErrors } from '../../shared/enums/auth-errors';
import { resolveServerErrorMessage } from '../http/server-error-message';
import { isImpersonationTargetNotFound } from '../http/impersonation-target-not-found';
import { ImpersonationNoticeService } from '../services/impersonation-notice.service';
import { environment } from 'src/environments/environment';

/**
 * `typeof x === 'object'` alone admits null and arrays; the diagnostic only ever reads keys off a plain object.
 * Takes `unknown` because it inspects a response body, which is whatever the server sent.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const IS_TOKEN_RETRY = new HttpContextToken<boolean>(() => false);

/**
 * Opt a single request out of the collapse below, so its caller receives the whole `HttpErrorResponse`
 * rather than a message.
 *
 * By default this interceptor reduces every unhandled status to a string. Callers across the app
 * depend on receiving a string, so that cannot simply be removed. What the string *contains* is
 * decided by `resolveServerErrorMessage` (`core/http/server-error-message.ts`), which is the single
 * normative statement of which API error shapes reach a user — read it before adding a shape.
 *
 * There are two distinct reasons to opt out, and they age differently:
 *
 * 1. **You need `err.status`.** The collapse destroys it irrecoverably and always will
 *    (`audit-trail.service.ts` branches on 404 versus 503). This reason is permanent.
 * 2. **You need the body's structure, not its prose.** Since #1634 the interceptor surfaces the
 *    server's own message on its own, so this reason alone is usually no longer sufficient —
 *    check whether a plain string would do before opting out.
 *
 * Statuses 0, 401 and 422 are unaffected: they already return the response untouched.
 */
export const PRESERVE_ERROR_RESPONSE = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ErrorInterceptorService implements HttpInterceptor {
  private isNavigatingToAuthError = false;

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly userService: UserService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly impersonationNotice: ImpersonationNoticeService
  ) {}

  // `unknown` rather than a modelled type: this runs for every request in the app, so the body genuinely
  // is opaque here — the true-boundary case the standards allow. Nothing below reads it.
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        switch (err.status) {
          case 0:
            this.userService.connectionIssueNotification();
            this.snackBar.open('Please check internet connection', 'Error', {
              duration: 3000,
            });
            return throwError(() => err);
          case 401: {
            if (request.context.get(IS_TOKEN_RETRY)) {
              this.navigateToAuthError(AuthErrors.RefreshTokenError);
              return throwError(() => err);
            }

            return this.authService.refreshTokenSilent().pipe(
              switchMap(() => {
                const retryRequest = this.authService.addAuthHeader(request).clone({
                  context: request.context.set(IS_TOKEN_RETRY, true),
                });
                return next.handle(retryRequest);
              }),
              catchError(() => {
                this.navigateToAuthError(AuthErrors.RefreshTokenError);
                return throwError(() => err);
              })
            );
          }
          case 422:
            return throwError(() => err);
          default:
            break;
        }
        if (request.context.get(PRESERVE_ERROR_RESPONSE)) {
          return throwError(() => err);
        }
        // #1634: surface the API's own explanation where it sent one. `err.message` ("Http failure
        // response for https://api-vision…: 400 Bad Request") and `err.statusText` ("Bad Request") are
        // deliberately no longer shown to anyone — they leak the internal host and route onto screens
        // staff read, and are useless as copy. The status and route are logged instead, on EVERY
        // collapsed error, so debugging loses nothing: the caller receives only a sentence, and
        // without this line the route would be gone. The middleware's own generic constant counts as
        // a server message, so gating the log on "nothing usable" would have silenced exactly the
        // commonest failure — an unhandled API exception.
        const { message, source } = resolveServerErrorMessage(err);
        // #1659: the member of staff being viewed as no longer exists. Most screens have no error branch
        // of their own, so without this the user would see empty cards and no reason; the view-as banner
        // shows the warning instead. The caller still receives the message as usual. Only Vision's own API
        // can raise it. A request sent with PRESERVE_ERROR_RESPONSE returned above and never reaches this;
        // none of the endpoints that send this refusal is called that way.
        if (
          err.status === 404 &&
          request.url.startsWith(environment.API_URL) &&
          isImpersonationTargetNotFound(message)
        ) {
          this.impersonationNotice.markTargetMissing();
        }
        // Deliberately not `err` wholesale, and deliberately not the body or the message. A body
        // rejected for being over-long is the case most likely to be a validation echo carrying
        // submitted personal data, and a browser console is captured by extensions and screen shares.
        //
        // The query string goes too, for the same reason: several search endpoints carry a learner
        // name or reference in it, and a failing search is exactly the sort of request that reaches
        // here. The path is what identifies the endpoint; the query is what identifies the person.
        const path = err.url == null ? '(unknown url)' : err.url.split('?')[0];
        // When the words shown were OURS rather than the server's, the body's SHAPE is appended,
        // because it names what the API sent (`{ message: … }` is the usual culprit — see core/http/).
        // For a plain object that is its keys — C# property names, never values; `typeof` alone
        // cannot tell `{}` from an array or null. For a validation body the useful keys are one level
        // down: `ValidationProblemDetails` nests them under `errors`, so those are what get named (a
        // bare `SerializableError` dictionary is keyed by field already). Still names only, never a
        // value. No `JSON.stringify`: a circular reference would throw INSIDE this catchError and
        // replace the user's message with a TypeError. A diagnostic must never be able to break the
        // path it is diagnosing. `unknown` for the same reason as `isRecord`: the body is whatever the server sent.
        const shapeOf = (body: unknown): string =>
          isRecord(body)
            ? `keys: ${Object.keys(body).join(', ') || '(none)'}`
            : `type: ${body === null ? 'null' : typeof body}`;
        const rejectedFields =
          source === 'validation' && isRecord(err.error) && isRecord(err.error['errors'])
            ? err.error['errors']
            : err.error;
        const detail =
          source === 'server'
            ? ''
            : source === 'validation'
              ? ` (validation body, our sentence shown; rejected ${shapeOf(rejectedFields)})`
              : ` (no usable server message; body ${shapeOf(err.error)})`;
        console.error(`HTTP ${err.status} ${path}${detail}`);
        return throwError(() => message);
      })
    );
  }

  private navigateToAuthError(error: AuthErrors): void {
    if (this.isNavigatingToAuthError) {
      return;
    }
    this.isNavigatingToAuthError = true;
    this.router.navigate(['auth-error'], { queryParams: { error } }).then(() => {
      this.isNavigatingToAuthError = false;
    });
  }
}
